import React, { useRef } from 'react';
import { MegarubigramStore } from '../MegarubigramStore';
import { flipSubgridVisualHorizontally, flipSubgridVisualVertically, getFlippedPositionalId, rotateSubgridVisual } from '../megarubigramUtils';
import { IconButton } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';

type ControlSubgridProps = {
  gridId: number;
  subgridId: number;
};

const ControlSubgrid = ({ gridId, subgridId, }: ControlSubgridProps) => {
  const grid = MegarubigramStore.useState(s => s.grids[gridId]);
  const subgrid = grid.subgrids[subgridId];
  const activeSubgridId = MegarubigramStore.useState(s => s.activeSubgrid);
  const isActive = subgridId === activeSubgridId;

  const subgridHFlipped = subgrid.flippedHorizontally
  const subgridVFlipped = subgrid.flippedVertically;
  const gridHFlipped = grid.flippedHorizontally;
  const gridVFlipped = grid.flippedVertically;

  // if it's flipped one way but not the other, then reverse rotation
  const reverseSubgridRotation = gridHFlipped !== gridVFlipped;

  const degreesSubgridRotated = subgrid.degreesRotated;
  const degreesGridRotated = grid.degreesRotated;
  const degreesPuzzleRotated = MegarubigramStore.useState(s => s.degreesRotated);
  const isSubgridAt90DegAngle =
    ((degreesSubgridRotated + degreesGridRotated + degreesPuzzleRotated) / 90) % 2 !== 0;

  const flipperStyles: React.CSSProperties = {
    transform: `rotateY(${subgridHFlipped ? 180 : 0}deg)`
      + ` rotateX(${subgridVFlipped ? 180 : 0}deg)`
      + ` rotateZ(${degreesSubgridRotated}deg)`
  };

  let degreesToRotateLetters = (-1) *
    (degreesSubgridRotated + degreesGridRotated + degreesPuzzleRotated);

  let flipsToAdd = 0;
  const isGridAt90DegAngle = Math.abs(degreesGridRotated + degreesPuzzleRotated) % 180 !== 0;

  if (isGridAt90DegAngle) {
    if (subgridVFlipped) flipsToAdd += 1;
    if (subgridHFlipped) flipsToAdd += 1;
    if (gridVFlipped) flipsToAdd += 1;
    if (gridHFlipped) flipsToAdd += 1;
  }
  // don't be deceived, degreesGridRotated is not the same as what makes up isGridAt90DegAngle
  if ((gridVFlipped !== gridHFlipped) && degreesGridRotated % 180 !== 0) flipsToAdd += 1;

  degreesToRotateLetters += (180 * (flipsToAdd % 2));

  // This makes sure letters never rotate more than 180deg in any direction for a transform
  const prevDegree = useRef<number>(degreesToRotateLetters);
  const equivalentDiff = (degreesToRotateLetters - prevDegree.current) % 360;

  if (equivalentDiff === 270) degreesToRotateLetters = prevDegree.current - 90;
  else if (equivalentDiff === -270) degreesToRotateLetters = prevDegree.current + 90;
  else if (equivalentDiff === 0) degreesToRotateLetters = prevDegree.current;
  else degreesToRotateLetters = prevDegree.current + equivalentDiff;

  prevDegree.current = degreesToRotateLetters;
  ///////// end that code ///////

  const letterStyles: React.CSSProperties = {
    transform: `rotate(${degreesToRotateLetters}deg)`
      + ` rotate3d(1, 0, 0, ${subgridVFlipped !== gridVFlipped ? 180 : 0}deg)`
      + ` rotate3d(0, 1, 0, ${subgridHFlipped !== gridHFlipped ? 180 : 0}deg)`,
  };

  ////////////////
  // This little piece of code is _essential_. It figures out what position
  // this subgrid is in relative to its parent grid. 0=top-left, 1=top-right,
  // 2=bottom-right, 3=bottom left. clockwise order.

  // We need this so the utils can update the right part of the char matrices
  // that are used to check if the puzzle is solved or not.

  // This variable doesn't include degreesSubgridRotated because that doesn't change where
  // the subgrid is, which is the point of this calculation.
  const timesSubgridPositionRotated = (degreesPuzzleRotated + degreesGridRotated) / 90;

  // This is how far the subgrid has moved around the grid.
  // From upper left to lower right would be 2 or -2, for example.
  const numSubgridPositionShifts = timesSubgridPositionRotated % 4;

  // this line accounts for rotations--
  const rotatedSubgridId = (((subgridId + numSubgridPositionShifts) % 4) + 4) % 4;
  // --and this line accounts for flips, giving a completely right positional id.
  const positionalSubgridId = getFlippedPositionalId(
    rotatedSubgridId, gridHFlipped, gridVFlipped, degreesPuzzleRotated,
  );

  ////////////////
  
  const onCellClick = (rowIndex: number, cellIndex: number) => {
    if (isSubgridAt90DegAngle) {
      if (rowIndex === 1 && [0,2].includes(cellIndex)) {
        flipSubgridVisualVertically(gridId, subgridId, positionalSubgridId);
      } else if ([0,2].includes(rowIndex) && cellIndex === 1) {
        flipSubgridVisualHorizontally(gridId, subgridId, positionalSubgridId);
      }
    } else {
      if (rowIndex === 1 && [0,2].includes(cellIndex)) {
        flipSubgridVisualHorizontally(gridId, subgridId, positionalSubgridId);
      } else if ([0,2].includes(rowIndex) && cellIndex === 1) {
        flipSubgridVisualVertically(gridId, subgridId, positionalSubgridId);
      }
    }
  };

  const isClickableCell = (rowIndex: number, cellIndex: number) => {
    return (rowIndex === 1 && [0,2].includes(cellIndex)) || ([0,2].includes(rowIndex) && cellIndex === 1);
  };

  return (
    <div className={`controlSubgrid subgrid${subgridId} ${isActive ? 'activeSubgrid' : ''}`}>
      <div className="controlSubgridFlipper" style={flipperStyles}>

        <div className={`controlSubgridContents ${subgrid.isInFlippingAnimation ? 'isFlipping' : ''}`}>
          {subgrid.displayContent.map((row, rowIndex) => (
            <div key={rowIndex} className="gridRow">
              {row.map((cell, cellIndex) => (
                <div key={cellIndex} className={`gridCellContainer ${isClickableCell(rowIndex, cellIndex) ? 'clickableCell': ''}`}
                  onClick={() => onCellClick(rowIndex, cellIndex)}
                >
                  {(rowIndex === 1 && cellIndex === 1) ?
                    <div className="gridCell" style={letterStyles}>
                      <IconButton disableRipple className="rotateSubgridClockwiseButton center" title="Rotate Clockwise"
                        onClick={() => rotateSubgridVisual(gridId, subgridId, positionalSubgridId, !reverseSubgridRotation)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          rotateSubgridVisual(gridId, subgridId, positionalSubgridId, reverseSubgridRotation);
                        }}
                      >
                        <ReplayIcon/>
                      </IconButton>
                    </div>
                    :
                    <div className={`gridCell ${cell === '.' ? 'blankCell' : ''}`}
                      style={cell === '.' ? {} : letterStyles}
                    >
                      {cell === '.' ? '' : cell}
                    </div>
                  }
                </div>
              ))}
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default ControlSubgrid;